import type {
    BacklogSeriesPoint,
    HistoryBucket
} from '../../../services/dmMonitoringOverviewHistory';
import { bucketLabel } from './overviewSeries.ts';
import type { PerPersonMeasure } from './perPersonRows.ts';

export type BacklogKey = 'notifications' | 'messages';

// Stack order, bottom first.
export const BACKLOG_KEYS: readonly BacklogKey[] = ['notifications', 'messages'];

export interface BacklogRow {
    bucketStart: string;
    // When the counts were measured: the bucket's end, or now for the current one.
    at: string;
    label: string;
    notifications: number | null;
    messages: number | null;
    // Null unless both counts are known.
    total: number | null;
}

export interface BacklogSpan {
    x1: string;
    x2: string;
}

type BacklogCounts = Pick<BacklogRow, 'notifications' | 'messages' | 'total'>;

function measuredCounts(point: BacklogSeriesPoint, measure: PerPersonMeasure): BacklogCounts {
    if (measure === 'patients') {
        const { patients } = point;
        return {
            notifications: patients?.notifications ?? null,
            messages: patients?.messages ?? null,
            total: patients?.total ?? null
        };
    }
    const { notifications, messages } = point;
    return {
        notifications,
        messages,
        total: notifications === null || messages === null ? null : notifications + messages
    };
}

export function backlogRows(
    series: readonly BacklogSeriesPoint[],
    bucket: HistoryBucket,
    measure: PerPersonMeasure
): BacklogRow[] {
    return series.map((point) => ({
        bucketStart: point.bucketStart,
        at: point.at,
        label: bucketLabel(point.bucketStart, bucket),
        ...measuredCounts(point, measure)
    }));
}

export function hasBacklogData(rows: readonly BacklogRow[]): boolean {
    return rows.some((row) => row.notifications !== null || row.messages !== null);
}

export function unreliableSpan(
    rows: readonly BacklogRow[],
    reliableFrom: string
): BacklogSpan | null {
    const cutoff = Date.parse(reliableFrom);
    const early = rows.filter((row) => Date.parse(row.at) < cutoff);
    if (early.length === 0) return null;
    return { x1: early[0].bucketStart, x2: early[early.length - 1].bucketStart };
}
